import React, { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PhotoLibrarySharpIcon from "@mui/icons-material/PhotoLibrarySharp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadIcon from "@mui/icons-material/Download";
import useProduct from "../hooks/useProduct";
import usePgiModules from "../hooks/useModules";
import { Helmet, HelmetProvider } from "react-helmet-async";
import useNews from "../hooks/useNews";
import CardNews from "../components/news/CardNews";
import CarouselMobile from "../components/carousel/CarouselMobile";
import ModuleBlockNews from "../components/news/ModuleBlockNews";
import { isVideov2 } from "../helpers/isVideo";
import { getURLFromImageBackground, sortAndFilterProducts } from "../helpers/functions";
import { formatUrl, formatDescription, URLTypeProduct } from "../helpers/functions";
import { getNewsByProduct, PRODUCTO_ESPECIAL, PRODUCTO_LINEA } from "../helpers/constants";
import toast from "react-hot-toast";

const ContainerInfo = lazy(() => import("../components/products/ContainerInfo"));
const StaticSlider = lazy(() => import("../components/products/StaticSlider"));
const ListaProducto = lazy(() => import("../components/products/ListaProducto"));
const FaqsDinamic = lazy(() => import("../components/faqs/FaqsDinamic"));
const Galeria3DModels = lazy(() => import("../components/galeria3d/Galeria3DModels"));
const BannerPub = lazy(() => import("../components/BannerPub"));

const Products = () => {
  const { getProductsByBody, product, getAllProducts, products } = useProduct();
  const params = useParams();
  const navigate = useNavigate();
  const [lowerTitle, setLowerTitle] = useState("");
  const [homeModules, setHomeModules] = useState([]);
  const { getModulesByParams } = usePgiModules();
  const [loading, setLoading] = useState(true);
  const { obtenerNoticias } = useNews();
  const [newsProduct, setNewsProduct] = useState([]);
  let descargasVisibles;
  useEffect(() => {
    getModulesByParams({ state: true }).then((res) => {
      setHomeModules(res.sort((a, b) => a.position - b.position));
    });
    getAllProducts({ onlyTitleAndIcon: true, visible: true });
  }, []);

  const titlePageUrl = (product) => {
    switch (product.title) {
      case "TENSOBLOCK":
        return `${product.title} - Bloque de hormigón para muros y cerramientos`;
      case "VIGUETAS T180":
        return `${product.title} - Vigueta autoportante de hormigón para losas`;
      case "CERCALITE":
        return `${product.title} - Cerco premoldeado de hormigón de Tensolite SA`;
      case "POSTES P10":
        return `${product.title} de Tensolite - Postes de hormigón para cercados`;
      case "TENSOQUINES":
        return `${product.title} - Adoquines de hormigón en diferentes modelos y colores`;
      case "VIGUETAS T21":
        return `${product.title} - Vigueta pretensada de hormigón para losas`;
      case "BOVEDILLA":
        return `${product.title} - Bovedillas EPS y de hormigón para losas`;
      case "TEJALITE":
        return `${product.title} de Tensolite SA - Tejas de hormigón de diferentes colores`;
      case "COLUMNAS":
        return `${product.title} de hormigón para tendido de redes aéreas de Tensolite SA`;
      case "LOSA ALVEOLAR":
        return `${product.title} pretensada de hormigón a medida de Tensolite`;
      case "VIGAS":
        return `${product.title} pretensadas de hormigón para puentes de Tensolite SA`;
      case "PASARELAS":
        return `${product.title} premoldeada de hormigón para cruces peatonales de Tensolite`;
      case "TRIBUNAS":
        return `${product.title} para estadios premoldeadas de hormigón de Tensolite SA`;
      case "ESTRUCTURAS":
        return `${product.title} premoldeadas de hormigón de Tensolite SA`;
      case "OTROS":
        return `Comederos, Tetrápodos, Pilotes, Monumentos - Piezas de hormigón a medida`;
      case "UNIBLOCK":
        return `${product.title} - Adhesivo para ladrillos y bloques de hormigón`
      default:
        return "Tensolite";
    }
  }

  useEffect(() => {
    if (product.publish === false || product.visible === false) {
      navigate('/error404', { replace: true, state: { status: 404 } });
    }
  }, [product])


  useEffect(() => {
    const newTitle = formatUrl(params.id);
    getProductsByBody({ title: newTitle });
    setLowerTitle(newTitle);
  }, []);

  useEffect(() => {
    const getProductsNews = async () => {
      try {
        const data = await obtenerNoticias(...getNewsByProduct(product?.title));
        setNewsProduct(data.allNews);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error('Error al obtener las noticias multimedia')
      }
    };
    getProductsNews();
  }, [product]);

  descargasVisibles = product.filename?.filter(
    (item) => item.state === true && item.deleted !== true
  );

  const AbrirGaleria = () => {
    product.imageGallery.length > 0 &&
      navigate("/galeria", {
        replace: true,
        state: {
          imageGallery: product.imageGallery,
          url: window.location.href
        }
      });
  };
  const imageUrl = product?.imageBackground && getURLFromImageBackground(product.imageBackground);
  const url = product?.title && formatUrl(product.title);
  const mainTypeURL = product && URLTypeProduct(product.type);

  return (
    <>
      <HelmetProvider>
        <Helmet prioritizeSeoTags>
          <title>{titlePageUrl(product)}</title>
          <meta name="title" content={titlePageUrl(product)} />
          <meta name="description" content={product?.top_mobile && product?.top_mobile?.length > 0 ? product.top_mobile : formatDescription(product?.top)} />

          <meta property="og:type" content="website" />
          <meta
            property="og:url"
            content={`https://tensolite.com/${mainTypeURL}/${url}`}
          />
          <meta property="og:title" content={titlePageUrl(product)} />
          <meta property="og:description" content={`${product?.top}`} />
          <meta property="og:image" content={`${imageUrl}`} />

          <meta property="twitter:card" content="summary_large_image" />
          <meta
            property="twitter:url"
            content={`https://tensolite.com/${mainTypeURL}/${url}`}
          />
          <meta property="twitter:title" content={titlePageUrl(product)} />
          <meta property="twitter:description" content={`${product?.top}`} />
          <meta property="twitter:image" content={`${imageUrl}`} />

          <script type="application/ld+json">
            {`
            "@context": "https://schema.org",
            "@type": "Product" ,
            "name": "${titlePageUrl(product)}",
            "description": "${product.top}",
            "url": "http://tensolite.com/${mainTypeURL}/${url}",
            "image": "${product.imageLogo}",
          `}
          </script>
        </Helmet>
      </HelmetProvider>
      <Suspense fallback={<div> </div>}>
        <section className="contenedor-principal">
          <StaticSlider product={product} />

          {product.type !== undefined && product.type === "productoEspecial" ? (
            <Galeria3DModels nameProd={product.title} desktop={true} />
          ) : (
            ""
          )}
          {product?.imageGallery?.length > 0 && product?.galleryEnabled && (
            <>
              <div
                onClick={AbrirGaleria}
                id="boton-galeria"
                className="description_desktop boton-galeria"
              >
                <p className="p-galeria">GALERÍA</p>
                <PhotoLibrarySharpIcon />
              </div>
            </>
          )}

          <ContainerInfo product={product} />
        </section>

        {product.type !== undefined && product.type === "productoEspecial" ? (
          <>
            <section id="bloq-inferior" className="container">
              <ul className="check-list ">
                {product?.benefit?.map((item, index) => (
                  <li key={`icon-benefit-${index}`} className="col-10 col-lg-6 col-md-6">
                    <span className="material-icons-outlined">
                      {item.iconBenefit ? item.iconBenefit : "check_box"}
                    </span>{" "}
                    {item.textBenefit}
                  </li>
                ))}
              </ul>
            </section>
          </>
        ) : (
          <>
            <section id="bloq-inferior" className="container">
              <ul className="check-list ">
                {product?.benefit?.map((item, index) => (
                  <li key={`icon-benefit-${index}`} className="col-10 col-lg-6 col-md-6">
                    <span className="material-icons-outlined">
                      {item.iconBenefit ? item.iconBenefit : "check_box"}
                    </span>{" "}
                    {item.textBenefit}
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
        <section className="">
          {product.imageProduct && (
            <div className="container bloque_img_product ">
              {" "}
              <div className="foto-instal image-1">
                {isVideov2(product.imageProduct ? product.imageProduct[0]?.url : "") ? (
                  <video
                    src={product.imageProduct ? product.imageProduct[0]?.url : ""}
                    autoPlay
                    muted
                    loop
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <img
                    src={product.imageProduct ? product.imageProduct[0]?.url : ""}
                    alt={product.imageProduct ? product.imageProduct[0]?.alt : product?.title}
                    title={product?.title}
                    className="image img-block"
                    loading="lazy"
                  />
                )}
              </div>
              <div className="foto-instal image-2">
                {isVideov2(product.imageProduct ? product.imageProduct[1]?.url : "") ? (
                  <video
                    src={product.imageProduct ? product.imageProduct[1]?.url : ""}
                    autoPlay
                    muted
                    loop
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <img
                    src={product.imageProduct ? product.imageProduct[1]?.url : ""}
                    alt={product.imageProduct ? product.imageProduct[1]?.alt : product?.title}
                    title={product?.title}
                    className={`${product.imageProduct[1] ? "img-block image " : "image"} `}
                    loading="lazy"
                  />
                )}
              </div>
            </div>
          )}

          {product.type !== undefined && product.type === "productoEspecial" ? (
            <div className="bg-break">
              <div className="container text-button">
                <article className="info-text col-12 col-md-12 col-lg-9 mb-4 pb-4">
                  <h3 className="padding_top">{product?.titleSubDescription}</h3>
                  <p
                    className="product-tiny-editor"
                    dangerouslySetInnerHTML={{
                      __html: product?.subdescription
                    }}
                  ></p>
                </article>
                <div role="presentation" className="settingscol-buttons col-3 columna-3">
                  <div className={` ${descargasVisibles?.length === 0 ? "d-none" : ""}`}>
                    <h3>Descargas</h3>
                    <ul className="settings__lista-descargas">
                      {product?.filename?.map(
                        (item, index) =>
                          item.state === true &&
                          item.deleted === false && (
                            <li key={`product-download-${index}`}>
                              <DownloadIcon />
                              <a
                                href={item?.url}
                                target="_blank"
                                rel="noreferrer"
                                title={`Descargar ${item.filename}`}
                              >
                                {item?.title}
                              </a>
                            </li>
                          )
                      )}
                    </ul>
                  </div>
                  <div className="">
                    {product.faqs?.length !== 0 && product.faqsEnabled && (
                      <FaqsDinamic vistaProducto={true} producto={product} />
                    )}
                    <button className="settings_button-border button-b-primary_2">
                      <a href="/contacto" title={`Dónde comprar ${product.title}`}>
                        Consulta técnica <ArrowForwardIcon />
                      </a>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-break">
              <div className="container text-button">
                <article className="info-text col-12 col-md-12 col-lg-9 pb-4">
                  <h3 className="padding_top text-center mb-3">{product?.titleSubDescription}</h3>
                  <p
                    className="product-tiny-editor"
                    dangerouslySetInnerHTML={{
                      __html: product?.subdescription
                    }}
                  ></p>
                </article>
                <div
                  role="presentation"
                  className="settings__col-buttons col-12 col-md-12 col-lg-3 "
                >
                  <div className={` ${descargasVisibles?.length === 0 ? "d-none" : ""}`}>
                    <h3>Descargas</h3>
                    <ul className="settings__lista-descargas">
                      {product?.filename?.map(
                        (item, index) =>
                          item.state === true &&
                          item.deleted === false && (
                            <li key={`product-download-${index}`}>
                              <DownloadIcon />
                              <a
                                href={item?.url}
                                target="_blank"
                                rel="noreferrer"
                                title={`Descargar ${item.filename}`}
                              >
                                {item?.title}
                              </a>
                            </li>
                          )
                      )}
                    </ul>
                  </div>
                  <div className="">
                    <button
                      className={`settings_button-pleno bg-p-primary_2 ${product.instalationStepsEnabled && product.instalationSteps?.length !== 0
                        ? ""
                        : "d-none"
                        }`}
                    >
                      <a href={`/instalacion/${lowerTitle}`} title={`Instalación de ${lowerTitle}`}>
                        Instalación <ArrowForwardIcon />
                      </a>
                    </button>
                    {product.faqs?.length !== 0 && product.faqsEnabled && (
                      <FaqsDinamic vistaProducto={true} producto={product} />
                    )}
                    <button className="settings_button-border button-b-primary_2">
                      <a href="/donde-comprar" title={`Dónde comprar ${product.title}`}>
                        DONDE COMPRAR <ArrowForwardIcon />
                      </a>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {homeModules.map((item, index) => {
          switch (item.title) {
            case "Banner_publicitario":
              if (item.visible.includes(`${product._id}`)) {
                return (
                  <React.Fragment key={`banner-pub-${index}`}>
                    <BannerPub idProducto={product._id} />
                  </React.Fragment>
                );
              }
              break;
            case "Bloque_info":
              if (item.visible.includes("home")) {
                return (
                  <React.Fragment key={`bloque-info-${index}`}>
                    <ModuleBlockNews idProducto={product._id} />
                  </React.Fragment>
                );
              }
              break;
            default:
          }
        })}
        {product?.subproduct
          ? product?.subproduct?.length !== 0 && (
            <div>
              <ListaProducto subProducts={product?.subproduct} titleProd={params.id} />
            </div>
          )
          : ""}
        {homeModules.map((item, index) => {
          switch (item.title) {
            case "Carrousel_deLinea":
              if (item.visible.includes(`${product._id}`)) {
                return (
                  <div key={`carousel-${index}`} className="container producto-linea">
                    <CarouselMobile isHomePage products={sortAndFilterProducts(products, PRODUCTO_LINEA)} />
                  </div>
                );
              }
              break;
            case "Carrousel_especial":
              if (item.visible.includes(`${product._id}`)) {
                return (
                  <div key={index} className="container producto-linea mb-5">
                    <CarouselMobile isHomePage isProductoEspecial products={sortAndFilterProducts(products, PRODUCTO_ESPECIAL)} />
                  </div>
                );
              }
              break;
            default:
          }
        })}
        {newsProduct?.length > 0 && (
          <>
            <div className="container">
              <div className="seccion-mas-noticias">
                <div className="title-noticias ">
                  <h3>Últimas noticias de {product.title}</h3>
                </div>
              </div>
              <div className="seccion-cards-noticias ">
                <div id="seccion-noticas">
                  <div className="">
                    <div className="masonry mansory_tensoblog_replica">
                      {loading ? (
                        <>
                          <CardNews loading={true} tensoblog={true} />
                          <CardNews loading={true} tensoblog={true} />
                        </>
                      ) : (
                        <>
                          {newsProduct?.map((noticia, index) => (
                            <CardNews key={`new-products-${index}`} noticia={noticia} />
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Suspense>
    </>
  );
};

export default Products;
